import React, { useState, useCallback, useEffect } from 'react'
import { message, Progress, Button, Input, Radio, Modal } from 'antd'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import {
  UploadOutlined,
  EyeOutlined,
  CloseOutlined,
  SyncOutlined,
  ThunderboltOutlined,
  ArrowRightOutlined
} from '@ant-design/icons'
import Dropzone from 'react-dropzone'
import PropTypes from 'prop-types'
import './UploadDocument.scss'
import StoryCreationForm from './StoryCreationForm'
import StepTitle from '../StepTitle/StepTitle'
import actions from '../../../../store/actions'
import attachIcon from './img/attach.svg'
import pdfIcon from './img/pdfIcon.svg'

const UploadDocument = ({
  formData,
  onValidationChange,
  setFormData,
  onProceed,
  library,
  upload,
  clearUploadList,
  videoId
}) => {
  const [isUploading, setIsUploading] = useState(false)
  const [isStoryFormVisible, setIsStoryFormVisible] = useState(false)
  const [inputMethod, setInputMethod] = useState('upload')
  const [directText, setDirectText] = useState('')

  // Initialize component state from formData
  useEffect(() => {
    const { file, voiceoverText } = formData
    if (!file && !voiceoverText) return

    if (voiceoverText && voiceoverText !== directText) {
      setDirectText(voiceoverText)
    }
    setInputMethod(file ? 'upload' : 'text')
  }, [formData])

  useEffect(() => {
    onValidationChange(!!formData.voiceoverText)
  }, [formData.voiceoverText])

  const onDropFile = async dropped => {
    clearUploadList()
    if (!dropped || dropped.length === 0) {
      return
    }
    const maxSize = 10 * 1024 * 1024 // 10MB limit
    const invalidFiles = dropped.filter(file => file.size > maxSize)

    if (invalidFiles.length > 0) {
      message.error('File size must be lower than 10 MB!')
      return
    }
    setIsUploading(true)

    try {
      const response = await upload(dropped[0])
      const newFile = {
        uid: response.id,
        name: response.filename,
        status: 'done',
        url: response.link,
        path: response.path
      }
      setFormData(prev => ({ ...prev, file: newFile }))
      // Open story form immediately after upload
      setIsStoryFormVisible(true)
    } catch (error) {
      message.error('Failed to upload file')
    } finally {
      setIsUploading(false)
    }
  }

  const handleRemove = file => {
    setFormData(prev => ({ ...prev, file: null }))
    onValidationChange(!!formData.voiceoverText)
  }

  const handleView = link => {
    window.open(link, '_blank').focus()
  }

  const handleCloseStoryForm = () => {
    setIsStoryFormVisible(false)
  }

  const handleAddStory = story => {
    setIsStoryFormVisible(false)
    setDirectText(story)
    setFormData(prev => ({
      ...prev,
      voiceoverText: story || directText,
      // Clear file if we're in text mode
      file: inputMethod === 'text' ? null : prev.file
    }))
    onValidationChange(true)
    // Removed onProceed() to prevent auto-advancing
  }

  const handleDirectTextChange = text => {
    setDirectText(text)
  }

  const handleOpenStoryForm = () => {
    if (inputMethod === 'text' && !directText.trim()) {
      message.error('Please enter your script text first')
      return
    }
    setIsStoryFormVisible(true)
  }

  return (
    <div className="upload-document">
      <StepTitle
        iconType="file-text"
        title="Upload Document"
        description="Please upload a PDF document with your video script or provide your
          script text directly. You'll need to finalize your script in the Story Form."
      />

      <div className="upload-document__input-selector">
        <Radio.Group
          value={inputMethod}
          onChange={e => setInputMethod(e.target.value)}
          buttonStyle="solid"
          size="large"
          className="upload-document__input-selector-radio"
        >
          <Radio.Button value="upload">PDF Document</Radio.Button>
          <Radio.Button value="text">Direct Text Input</Radio.Button>
        </Radio.Group>
      </div>

      {inputMethod === 'upload' ? (
        <div className="upload-file-main">
          {!formData.file ? (
            <Dropzone
              accept="application/pdf"
              multiple={false}
              onDrop={onDropFile}
              disabled={isUploading}
              className="upload-file-main-action"
            >
              {({ getRootProps, getInputProps, isDragActive }) => (
                <div
                  className={`upload-file-main-action ${
                    isDragActive ? 'active' : ''
                  }`}
                >
                  <div className="upload-file-main-action-right">
                    <div className="upload-file-main-action-right-icon">
                      <img src={attachIcon} alt="attachIcon" />
                    </div>
                    <div className="upload-file-main-action-right-content">
                      <div className="upload-file-main-action-right-content-title">
                        Add File
                      </div>
                      <div className="upload-file-main-action-right-content-size">
                        10 MB, maximum file size
                      </div>
                    </div>
                  </div>

                  <div className="upload-file-main-action-btn">
                    {isUploading ? (
                      <SyncOutlined
                        spin
                        style={{ color: '#5B6EE2', fontSize: 16 }}
                      />
                    ) : (
                      <UploadOutlined
                        style={{ color: '#5B6EE2', fontSize: 16 }}
                      />
                    )}
                  </div>
                </div>
              )}
            </Dropzone>
          ) : (
            <div className="upload-file-main-filediv">
              <div className="upload-file-main-filediv-right">
                <div className="upload-file-main-filediv-right-icon">
                  <img src={pdfIcon} alt="pdfIcon" />
                </div>
                <div className="upload-file-main-filediv-right-content">
                  <div>
                    <div className="upload-file-main-filediv-right-content-title">
                      {formData.file.name}
                    </div>
                  </div>
                </div>
              </div>

              <div className="upload-file-main-filediv-btn">
                <div>
                  <EyeOutlined
                    style={{ color: '#5B6EE2', fontSize: 16 }}
                    onClick={() => handleView(formData.file.url)}
                  />
                </div>
                <div>
                  <CloseOutlined
                    style={{ color: 'red', fontSize: 16 }}
                    onClick={() => handleRemove(formData.file)}
                  />
                </div>
              </div>
            </div>
          )}

          {isUploading &&
            library.uploadProgress.map((file, i) => (
              <div key={i}>
                <div className="upload-file-main-filediv">
                  <div className="upload-file-main-filediv-right">
                    <div className="upload-file-main-filediv-right-icon">
                      <img src={pdfIcon} alt="pdfIcon" />
                    </div>
                    <div className="upload-file-main-filediv-right-content">
                      <div>
                        <div className="upload-file-main-filediv-right-content-title">
                          {file.name}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {file.percent > 0 && <Progress percent={file.percent} />}
              </div>
            ))}

          {formData.file && (
            <Button
              className="upload-document__button"
              type="primary"
              onClick={() => setIsStoryFormVisible(true)}
            >
              <ThunderboltOutlined /> Generate Script
            </Button>
          )}
        </div>
      ) : (
        <div className="upload-document__text-input">
          <Input.TextArea
            value={directText}
            onChange={e => handleDirectTextChange(e.target.value)}
            placeholder="Please enter your video script text here"
            autoSize={{ minRows: 8, maxRows: 20 }}
            className="upload-document__textarea"
          />
          <div className="upload-document__text-actions">
            <Button
              type="primary"
              onClick={handleOpenStoryForm}
              className="upload-document__confirm-button"
              disabled={!directText?.trim()}
            >
              <ThunderboltOutlined />
              Generate Story with AI
            </Button>

            <Button
              type="default"
              onClick={() => handleAddStory()}
              className="upload-document__confirm-button"
              disabled={!directText?.trim()}
              style={{ marginRight: 8 }}
            >
              Use Text as Story <ArrowRightOutlined />
            </Button>
          </div>
        </div>
      )}

      {isStoryFormVisible && (
        <StoryCreationForm
          inputMethod={inputMethod}
          uploadedFile={inputMethod === 'upload' ? formData.file : null}
          initialText={directText}
          onCancel={handleCloseStoryForm}
          onAddStory={handleAddStory}
          videoId={videoId}
        />
      )}
    </div>
  )
}

UploadDocument.propTypes = {
  onValidationChange: PropTypes.func.isRequired,
  formData: PropTypes.object.isRequired,
  setFormData: PropTypes.func.isRequired,
  onProceed: PropTypes.func.isRequired,
  library: PropTypes.object.isRequired,
  upload: PropTypes.func.isRequired,
  clearUploadList: PropTypes.func.isRequired
}

const mapStateToProps = state => ({
  library: state.library
})

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      upload: actions.library.upload,
      clearUploadList: actions.library.clearUploadList
    },
    dispatch
  )

export default connect(mapStateToProps, mapDispatchToProps)(UploadDocument)
